import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import ValueMappers from '../ValueMappers';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { X, Check } from '@lifeomic/chromicons';
import { defineMessages, injectIntl, WrappedComponentProps } from 'react-intl';
import isNil from 'lodash/isNil';
import getSurveyItemTestId from '../../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../../util/testProps';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import clsx from 'clsx';

export const testIds = {
  textField: (linkId: string) => getSurveyItemTestId(linkId, 'textField'),
  cancelInput: (linkId: string) => getSurveyItemTestId(linkId, 'cancelInput'),
  confirmInput: (linkId: string) => getSurveyItemTestId(linkId, 'confirmInput'),
};

const messages = defineMessages({
  label: {
    id: 'surveyBuilder.openChoiceInput.label',
    defaultMessage: 'Custom selection value',
  },
  helpMessage: {
    id: 'surveyBuilder.openChoiceInput.helpMessage',
    defaultMessage: 'Enter a custom value',
  },
  cancel: {
    id: 'surveyBuilder.openChoiceInput.cancel',
    defaultMessage: 'Cancel',
  },
  complete: {
    id: 'surveyBuilder.openChoiceInput.complete',
    defaultMessage: 'Complete',
  },
});

// note that codings are not supported for open input.  This should be restricted at the config level
export type SupportedOpenChoiceTypes = 'string' | 'integer';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
    },
    textField: {
      flex: 1,
      marginRight: theme.spacing(1),
    },
    cancelButton: {
      marginRight: theme.spacing(1),
    },
  });

interface OpenInputOwnProps {
  itemType: SupportedOpenChoiceTypes;
  onCancel: () => void;
  onComplete: (val: string | number) => void;
  linkId?: string;
  className?: string;
}

interface OpenInputState {
  textFieldValue: any;
}

type OpenInputProps = OpenInputOwnProps &
  WrappedComponentProps &
  WithStyles<typeof styles>;

class OpenInput extends PureComponent<OpenInputProps, OpenInputState> {
  state = { textFieldValue: null as any };

  getValueMapper = () => {
    const type = this.props.itemType;

    switch (type) {
      case 'string':
        return ValueMappers.stringTextField;
      case 'integer':
        return ValueMappers.integerTextField;
    }
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let newValue = null as string;
    if (e && e.target) newValue = e.target.value;

    this.setState({ textFieldValue: newValue });
  };

  getValue = () => {
    const mapper = this.getValueMapper();
    return mapper.toInputValue(this.state.textFieldValue as never);
  };

  handleCompleteClick = () => {
    const mapper = this.getValueMapper();
    const mappedValue = mapper.fromInputValue(
      this.state.textFieldValue,
      null as never, // not required for string or number
    );
    this.props.onComplete(mappedValue);
  };

  render() {
    const {
      itemType,
      classes,
      onCancel,
      intl: { formatMessage },
      linkId,
      className,
    } = this.props;

    const type = itemType === 'integer' ? 'number' : null;

    return (
      <div className={clsx(classes.container, className)}>
        <TextField
          className={clsx(className, classes.textField)}
          label={formatMessage(messages.label)}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          type={type}
          value={this.getValue()}
          onChange={this.handleChange}
          helpMessage={formatMessage(messages.helpMessage)}
          fullWidth
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          {...getTestProps(testIds.textField(linkId))}
        />
        <IconButton
          aria-label={formatMessage(messages.cancel)}
          size={0}
          icon={X}
          className={classes.cancelButton}
          onClick={onCancel}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          {...getTestProps(testIds.cancelInput(linkId))}
        />
        <IconButton
          aria-label={formatMessage(messages.complete)}
          size={0}
          icon={Check}
          onClick={this.handleCompleteClick}
          disabled={isNil(this.state.textFieldValue)}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          {...getTestProps(testIds.confirmInput(linkId))}
        />
      </div>
    );
  }
}

export default withStyles(styles)(injectIntl(OpenInput));
